/**
 * Session Expiration Handler v4.0 (Opaque Session)
 *
 * Simplified for opaque session auth — the backend handles all token management.
 * This component only monitors for 401s on API calls and redirects to /login.
 * No ConnectivityStateManager, LogoutDecisionEngine, or AuthRefreshManager needed.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { queryClient } from '@/lib/queryClient';

export function SessionExpirationHandler() {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const { showError } = useToast();

  const hasShownExpiration = useRef(false);
  const isProcessingLogout = useRef(false);

  const handleLogout = useCallback(async () => {
    if (hasShownExpiration.current || isProcessingLogout.current) return;

    isProcessingLogout.current = true;
    hasShownExpiration.current = true;

    // Cancel all in-flight queries to stop retries
    queryClient.cancelQueries();
    queryClient.clear();

    showError('Session Expired', 'Your session has expired. Please log in again.');

    try {
      await logout();
    } catch (error) {
      console.error('[SessionHandler] Logout failed:', error);
    }

    navigate('/login', { replace: true });

    // Refs stay latched until the user logs in again. Resetting them in the
    // same microtask as navigate() races with the route commit — any in-flight
    // fetch that resolves with 401 in that window would retrigger handleLogout,
    // stack another toast, and refire logout. The useEffect below clears
    // these flags once isAuthenticated becomes true (successful login).
  }, [logout, navigate, showError]);

  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      let response: Response;

      try {
        response = await originalFetch(...args);
      } catch (fetchError) {
        throw fetchError;
      }

      // Check if response is 401 from a non-auth endpoint
      if (response.status === 401 && isAuthenticated && !hasShownExpiration.current && !isProcessingLogout.current) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';

        // Skip auth endpoints to avoid loops
        if (!url.includes('/auth/') && !url.includes('/logout') && !url.includes('/health')) {
          await handleLogout();
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticated, handleLogout]);

  // Reset flags when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      hasShownExpiration.current = false;
      isProcessingLogout.current = false;
    }
  }, [isAuthenticated]);

  return null;
}
